import { Component, OnInit } from '@angular/core';
import {
  CampusWardsDataService,
  StudentListing,
  CampusWard,
  ConfirmDialog,
  SortService,
  UserService
} from '../../shared';
import { SelectableLocalStudents } from './selectable-local-students.model';
import { LeaderComponent } from '../leader.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './local-student-management.component.html',
  styleUrls: ['./local-student-management.component.scss']
})
export class LocalStudentManagementComponent extends LeaderComponent
  implements OnInit {
  homeWardWithStudentsList: SelectableLocalStudents[];
  studentsToBeAddedToCampusWard: StudentListing[] = [];
  currentStudentsInCampusWard: StudentListing[] = [];
  currentStudentsInCampusWardToRemove: StudentListing[] = [];
  studentsToEmail: StudentListing[] = [];

  ward: CampusWard;

  loading = false;
  loadingError = false;

  updating = false;
  updated = false;

  removing = false;
  removed = false;

  constructor(
    protected userService: UserService,
    protected dialog: MatDialog,
    private wardService: CampusWardsDataService,
    private sorter: SortService
  ) {
    super(userService, dialog);
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.isWardLeader()) {
      this.loadWardInformation(this.getWard());
    }
  }

  /**
   * Handles a stake leader wanting to look at local students of a ward in their stake.
   */
  onWardSelected(ward: CampusWard) {
    this.loadWardInformation(ward);
  }

  /**
   * Handles updating our list of students to remove/add based on the selection.
   */
  onSelectedOptionsChange(event: MatSelectionListChange) {
    if (event.options.length === 0) {
      return;
    }
    
    const student: StudentListing = event.options[0].value;

    if (event.options[0].selected) {
      if (this.currentStudentsInCampusWard.indexOf(student) === -1) {
        this.studentsToBeAddedToCampusWard.push(student);
        this.studentsToBeAddedToCampusWard.sort(
          this.sorter.sortStudentListingsAsc
        );
      } else {
        this.currentStudentsInCampusWardToRemove.splice(
          this.currentStudentsInCampusWardToRemove.indexOf(student),
          1
        );
      }
    } else {
      if (this.currentStudentsInCampusWard.indexOf(student) !== -1) {
        this.currentStudentsInCampusWardToRemove.push(student);
        this.currentStudentsInCampusWardToRemove.sort(
          this.sorter.sortStudentListingsAsc
        );
      } else if (this.studentsToBeAddedToCampusWard.indexOf(student) !== -1) {
        this.studentsToBeAddedToCampusWard.splice(
          this.studentsToBeAddedToCampusWard.indexOf(student),
          1
        );
      }
    }
  }

  /**
   * Adds the students as attending our ward; sets their local ward as our campus ward.
   */
  updateStudentsCampusWard() {
    this.resetUpdatedRemovedFlags();

    this.updating = true;
    this.wardService
      .updateStudentsCampusWard(
        this.studentsToBeAddedToCampusWard,
        this.ward,
        false
      )
      .pipe(
        finalize(() => (this.updating = false))
      )
      .subscribe(
        () => {
          this.studentsToBeAddedToCampusWard.forEach(student => {
            this.currentStudentsInCampusWard.push(student);
          });
          this.studentsToBeAddedToCampusWard = [];
          this.updated = true;
        },
        error => this.showError(error)
      );
  }

  /**
   * Removes the students from attending our ward; clears their local ward.
   */
  removeStudentsInCampusWard() {
    const pluralizedPart =
      this.currentStudentsInCampusWardToRemove.length === 1
        ? 'this student'
        : `these ${this.currentStudentsInCampusWardToRemove.length} students`;
    const message = `Are you sure you want to remove ${pluralizedPart} from ${
      this.ward.name
    }? This will also remove the students from any groups they currently belong to.`;
    const confirmDialog = this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: `Remove Students`, message: message }
    });

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.resetUpdatedRemovedFlags();
        this.removing = true;
        this.wardService
          .updateStudentsCampusWard(
            this.currentStudentsInCampusWardToRemove,
            this.ward,
            true
          )
          .pipe(
            finalize(() => (this.removing = false))
          )
          .subscribe(
            () => {
              this.currentStudentsInCampusWardToRemove.forEach(student => {
                const s = this.currentStudentsInCampusWard.indexOf(student);
                if (s !== -1) {
                  this.currentStudentsInCampusWard.splice(s, 1);
                }
              });
              this.currentStudentsInCampusWardToRemove = [];
              this.removed = true;
            },
            error => this.showError(error)
          );
      }
    });
  }

  /**
   * Resets our flags that indicate an update was done or a removal was done.
   */
  private resetUpdatedRemovedFlags() {
    this.removed = false;
    this.updated = false;
  }

  /**
   * Loads the ward's information: assigned local wards and possible students for each one.
   */
  private loadWardInformation(newWard: CampusWard) {
    this.resetUpdatedRemovedFlags();
    this.ward = newWard;

    // only load things for a singles ward
    if (this.ward != null && !this.ward.isForMarriedStudents) {
      this.loading = true;
      this.loadingError = false;

      this.wardService
        .getPossibleLocalStudents(this.ward.campusWardId)
        .pipe(
          finalize(() => (this.loading = false))
        )
        .subscribe(results => {
          // sort first, then transform to an object that lets us track what students the user has selected
          results.sort(this.sorter.sortHomeWardWithStudentsAsc);
          this.homeWardWithStudentsList = results.map(
            s => new SelectableLocalStudents(s)
          );

          // find the students that are already marked as attending our ward
          this.homeWardWithStudentsList.forEach(ward => {
            ward.homeWardStudents.students.forEach(s => {
              this.studentsToEmail.push(s);
            });
            ward.selectedStudents = ward.homeWardStudents.students.filter(
              student => student.isAttending
            );
            ward.selectedStudents.forEach(selected => {
              this.currentStudentsInCampusWard.push(selected);
            });

            // sort the students for display
            ward.homeWardStudents.students.sort(
              this.sorter.sortStudentListingsAsc
            );
          });
        }, error => (this.loadingError = true));
    }
  }
}
